import React, { FC, useState, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import Animated, { SharedValue, useAnimatedStyle, runOnJS } from 'react-native-reanimated';
import { PictureProps } from '@v2/components/imageHandling/components/Picture';
import ColorWrapper from '@styles/colors';
import { ColorFunction } from '@styles/colors/types';
import { font as initialFont } from '@styles/fonts';
import { fullWidth, scale as initialScale } from '@components/frame';
import ThumbnailItem from './ThumbnailItem';
import { isWeb } from '@helpers';
import { useSmallSliderCalculations } from './hooks/useSmallSliderCalculations';
import { ACTIVE_THUMBNAIL_HEIGHT } from '@v2/components/imageHandling/constants';

interface SmallSliderProps extends Pick<PictureProps, 'images' | 'width' | 'selectedIndex' | 'scale' | 'font'> {
  onSelectImage?: (index: number) => void;
  C: ColorFunction;
  bg: string;
  scrollProgress?: SharedValue<number>;
}

const SmallSlider: FC<SmallSliderProps> = ({
  images,
  width = fullWidth,
  selectedIndex,
  bg,
  scale = initialScale,
  font = initialFont,
  onSelectImage,
  C,
  scrollProgress,
}) => {
  if (!images || images.length <= 1) return null;

  const { animatedTranslateX, isFirstAnimationComplete } = useSmallSliderCalculations({
    imagesLength: images.length,
    selectedIndex,
    scrollProgress,
    width,
    scale,
  });

  const animatedSliderStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: animatedTranslateX.value }],
  }));

  const handleImagePress = (index: number) => onSelectImage?.(index);

  if (!isFirstAnimationComplete) {
    // for background consistency
    return (
      <View
        style={{
          borderTopColor: C('Hairline/Light/No trans'),
          borderTopWidth: StyleSheet.hairlineWidth,
          backgroundColor: bg,
          height: scale(ACTIVE_THUMBNAIL_HEIGHT),
          justifyContent: 'center',
          alignItems: 'center',
          width,
        }}
      />
    );
  }

  return (
    <View
      style={{
        backgroundColor: bg,
        height: scale(ACTIVE_THUMBNAIL_HEIGHT),
        justifyContent: 'center',
        alignItems: 'center',
        width,
        opacity: isFirstAnimationComplete ? 1 : 0,
      }}
    >
      <View
        style={{
          borderTopColor: C('Hairline/Light/No trans'),
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: StyleSheet.hairlineWidth,
          borderTopWidth: StyleSheet.hairlineWidth,
        }}
      />

      <View style={[{ width: '100%' }, isWeb && { alignItems: 'center' }]}>
        <Animated.View style={[animatedSliderStyle]}>
          <Animated.View style={{ flexDirection: 'row-reverse', alignItems: 'center', paddingVertical: scale(15) }}>
            {images.map((item, index) => (
              <ThumbnailItem
                key={item.contentHash}
                image={item}
                index={index}
                selectedIndex={selectedIndex}
                scrollProgress={scrollProgress}
                totalImages={images.length}
                onPress={handleImagePress}
                scale={scale}
                font={font}
                C={C}
              />
            ))}
          </Animated.View>
        </Animated.View>
      </View>

      <View
        style={{
          position: 'absolute',
          top: -scale(5),
          width: 0,
          height: 0,
          backgroundColor: 'transparent',
          borderStyle: 'solid',
          borderTopWidth: 0,
          borderTopColor: 'transparent',
          borderRightColor: 'transparent',
          borderLeftColor: 'transparent',
          borderBottomColor: C('System/Light/Blue'),
          borderLeftWidth: scale(9),
          borderRightWidth: scale(9),
          borderBottomWidth: scale(5),
          left: '50%',
          marginLeft: -scale(9),
        }}
      />
    </View>
  );
};

export default ColorWrapper(SmallSlider);
